import { Divider, Skeleton, Space, Tooltip } from "antd"
import { Proposal, ProposalJobData, hasActiveAutomations } from "./proposalsModel"
import { useProposalsInvalidator, useProposalsQuery } from "./proposalsQueries"
import { useEffect, useState } from "react"
import ProposalsPastVersions from "./ProposalsPastVersions"
import LatestProposal from "./LatestProposal"
import { faFile, faFileInvoiceDollar } from "@fortawesome/pro-solid-svg-icons"
import IconAndTextButton from "../../IconAndTextButton"
import CreateProposalModal from '../../Proposals/CreateProposalModal'
import { ProjectTypes } from '../../../common/constants'

export type Props = {
  job: ProposalJobData
  onAutomationChange?: (active: boolean) => void
  onLinkCustomerToQbo?: () => void
  onLinkCostCodeToQbo?: (costCodeId: number) => void
}

const Proposals = ({ job, onAutomationChange, onLinkCustomerToQbo, onLinkCostCodeToQbo }: Props) => {
  const [showPastProposals, setShowPastProposals] = useState(false);
  const proposalsInvalidator = useProposalsInvalidator()
  const proposalsQuery = useProposalsQuery(job?.id)
  const proposals: Proposal[] = proposalsQuery.data ?? []
  const latestProposal = proposals.sort((a, b) => b.id - a.id)[0] ?? undefined
  const pastProposals = proposals.filter(x => x.id !== latestProposal?.id)

  const [createProposalModalOpen, setCreateProposalModalOpen] = useState(false)

  const invalidateJob = () => {
    proposalsInvalidator.invalidate(job.id)
  }

  useEffect(() => {
    if (job != undefined) {
      proposalsInvalidator.invalidate(job.id)
    }
  }, [job])

  useEffect(() => {
    if (latestProposal != undefined) {
      onAutomationChange?.(hasActiveAutomations(latestProposal))
    }
  }, [latestProposal])

  const handleCreateNewProposal = () => {
    setCreateProposalModalOpen(true)
  }

  const handleSaveProposal = () => {
    setCreateProposalModalOpen(false)
    proposalsInvalidator.invalidate(job.id)
  }

  const handleLinkCustomerToQbo = () => {
    setCreateProposalModalOpen(false)
    onLinkCustomerToQbo?.()
  }

  const handleLinkCostCodeToQbo = (costCodeId: number) => {
    setCreateProposalModalOpen(false)
    onLinkCostCodeToQbo?.(costCodeId)
  }

  if (proposalsQuery.isLoading) {
    return <Skeleton active loading={true} />
  }

  const createProposalModal = <CreateProposalModal
    jobId={job?.id}
    open={createProposalModalOpen}
    onSave={() => handleSaveProposal()}
    onCancel={() => setCreateProposalModalOpen(false)}
    onLinkCustomerToQbo={handleLinkCustomerToQbo}
    onLinkCostCodeToQbo={handleLinkCostCodeToQbo}
    hideDownPayment={job?.type === ProjectTypes.Maintenance}
  />

  if (latestProposal === undefined) {
    return <div>
      <Tooltip title={job?.customer?.isActive ? "Create New Proposal" : "Proposal creation is disabled due to the customer being inactive."}>
        <Space>
          <IconAndTextButton
            type="primary"
            size="large"
            style={{ marginLeft: '5px' }}
            onClick={handleCreateNewProposal}
            icon={faFileInvoiceDollar}
            text="Create New Proposal"
            disabled={!job?.customer?.isActive}
          />
        </Space>
      </Tooltip>
      {createProposalModal}
    </div>
  }

  return <div>
    <LatestProposal
      latestProposal={latestProposal}
      onCreateNewProposal={handleCreateNewProposal}
      onInvalidateJob={invalidateJob}
      job={job}
    />
    <Divider></Divider>
    {pastProposals.length > 0 && <IconAndTextButton
      type="primary"
      size="large"
      onClick={() => setShowPastProposals(!showPastProposals)}
      icon={faFile}
      text="Show Previous Versions"
    />}
    {showPastProposals && <Divider />}
    <ProposalsPastVersions
      loading={proposalsQuery.isLoading}
      pastProposals={pastProposals}
      showPastProposals={showPastProposals}
    />
    {createProposalModal}
  </div>
}

export default Proposals
